/**
 * floorplan-v1 JSON import (spec 2026-08-22-03 §7).
 *
 * The bridge from `realassets-exp` exports — and from any future extraction
 * job — into the per-asset model: a validated document becomes **one all-in-one
 * vector representation** on the context asset, which promotion (§3) then
 * splits interactively.
 *
 * Validation is client-side and shaped after
 * `realassets-exp/floorplan/floorplan-schema.json`. Issues are codes, never
 * prose: the React shell translates `planEditor.import_errors.<code>`.
 *
 * Pure TypeScript — no React, no DOM.
 */
import { projectToLine, toDeg, type Point } from './geometry'
import type { VectorValue } from './scene'

export interface ImportIssue {
    code: string
    params?: Record<string, string | number>
}

export interface ImportResult {
    value: VectorValue
    /** What was read, for the confirmation the shell shows. */
    counts: { walls: number; spaces: number; openings: number; equipments: number }
    title: string
    /** Non-fatal remarks (dropped element, unresolved wall reference, …). */
    warnings: ImportIssue[]
}

/** Millimetres per unit, for every unit floorplan-v1 accepts. */
const UNIT_FACTORS: Record<string, number> = { mm: 1, cm: 10, m: 1000, in: 25.4, ft: 304.8 }

const isPoint = (v: unknown): v is Point => {
    const p = v as Point | undefined
    return !!p && Number.isFinite(p.x) && Number.isFinite(p.y)
}

const num = (v: unknown): number | undefined => (typeof v === 'number' && Number.isFinite(v) ? v : undefined)

interface RawWall {
    id?: unknown
    start?: unknown
    end?: unknown
    thickness?: unknown
    properties?: Record<string, unknown>
}

interface RawOpening {
    id?: unknown
    type?: unknown
    kind?: unknown
    wallId?: unknown
    wall_id?: unknown
    position?: unknown
    width?: unknown
    rotation?: unknown
    operation?: unknown
    leaves?: unknown
    swing?: unknown
    properties?: Record<string, unknown>
}

interface RawSpace {
    id?: unknown
    name?: unknown
    boundary?: unknown
    properties?: Record<string, unknown>
}

interface RawEquipment {
    id?: unknown
    type?: unknown
    kind?: unknown
    position?: unknown
    rotation?: unknown
    rotation_deg?: unknown
    dimensions?: { width?: unknown; depth?: unknown }
}

interface RawFloorplan {
    version?: unknown
    metadata?: { name?: unknown; generator?: unknown; units?: unknown }
    walls?: unknown
    spaces?: unknown
    openingEquipments?: unknown
    equipments?: unknown
}

const OPERATIONS = new Set(['hinged', 'sliding', 'pocket', 'folding', 'fixed', 'none'])
const SWINGS = new Set(['left', 'right'])

/**
 * Parses and validates a floorplan-v1 document, returning either the
 * representation value to create or the list of reasons it was refused.
 */
export function parseFloorplanV1(raw: unknown): { ok: true; result: ImportResult } | { ok: false; errors: ImportIssue[] } {
    const errors: ImportIssue[] = []
    const warnings: ImportIssue[] = []

    if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
        return { ok: false, errors: [{ code: 'not_an_object' }] }
    }
    const doc = raw as RawFloorplan

    // `version` is required by the schema and is the only cheap way to tell a
    // floorplan apart from any other JSON somebody drags onto the stage.
    if (typeof doc.version !== 'string' || !doc.version.trim()) {
        errors.push({ code: 'missing_version' })
    } else if (!/^(1(\.\d+)?|floorplan-v1)$/.test(doc.version.trim())) {
        errors.push({ code: 'unsupported_version', params: { version: doc.version } })
    }

    const rawWalls = Array.isArray(doc.walls) ? (doc.walls as RawWall[]) : null
    if (!rawWalls) errors.push({ code: 'missing_walls' })

    const unitsRaw = typeof doc.metadata?.units === 'string' ? doc.metadata.units : 'mm'
    const factor = UNIT_FACTORS[unitsRaw]
    if (factor === undefined) errors.push({ code: 'unsupported_units', params: { units: unitsRaw } })

    if (errors.length) return { ok: false, errors }

    const k = factor as number
    const scalePoint = (p: Point): Point => ({ x: round(p.x * k), y: round(p.y * k) })

    const walls: NonNullable<VectorValue['walls']> = []
    const wallsById = new Map<string, { start: Point; end: Point; thickness: number }>()
    const takenIds = new Set<string>()
    const idFor = (candidate: unknown, prefix: string, index: number): string => {
        let id = typeof candidate === 'string' && candidate.trim() ? candidate.trim() : `${prefix}-${index + 1}`
        while (takenIds.has(id)) id = `${id}-${index + 1}`
        takenIds.add(id)
        return id
    }

    ;(rawWalls ?? []).forEach((w, i) => {
        if (!isPoint(w.start) || !isPoint(w.end)) {
            warnings.push({ code: 'wall_dropped', params: { index: i + 1 } })
            return
        }
        const id = idFor(w.id, 'wall', i)
        const start = scalePoint(w.start as Point)
        const end = scalePoint(w.end as Point)
        const thickness = round((num(w.thickness) ?? 100 / k) * k)
        walls.push({ id, start, end, thickness, ...(w.properties ? { properties: w.properties } : {}) })
        wallsById.set(id, { start, end, thickness })
        // The original id is what openings reference, even when it was renamed.
        if (typeof w.id === 'string' && w.id.trim() && w.id.trim() !== id) {
            wallsById.set(w.id.trim(), { start, end, thickness })
        }
    })

    const spaces: NonNullable<VectorValue['spaces']> = []
    for (const [i, s] of asArray<RawSpace>(doc.spaces).entries()) {
        const ring = Array.isArray(s.boundary) ? (s.boundary as unknown[]).filter(isPoint).map(scalePoint) : []
        if (ring.length < 3) {
            warnings.push({ code: 'space_dropped', params: { index: i + 1 } })
            continue
        }
        spaces.push({
            id: idFor(s.id, 'space', i),
            ...(typeof s.name === 'string' ? { name: s.name } : {}),
            boundary: ring,
            ...(s.properties ? { properties: s.properties } : {}),
        })
    }

    const openings: NonNullable<VectorValue['openingEquipments']> = []
    for (const [i, o] of asArray<RawOpening>(doc.openingEquipments).entries()) {
        const kind = typeof o.kind === 'string' ? o.kind : typeof o.type === 'string' ? o.type : 'door'
        const wallId = typeof o.wallId === 'string' ? o.wallId : typeof o.wall_id === 'string' ? o.wall_id : undefined
        const host = wallId ? wallsById.get(wallId) : undefined
        const width = round((num(o.width) ?? 900 / k) * k)
        const out: NonNullable<VectorValue['openingEquipments']>[number] = {
            id: idFor(o.id, kind === 'window' ? 'window' : 'door', i),
            kind,
            width,
        }
        // floorplan-v1 gives the opening's centre as a *point*; the per-asset
        // model wants the normalized abscissa along its wall, and keeps the
        // absolute placement too so the element renders on its own (§3).
        if (isPoint(o.position)) {
            const centre = scalePoint(o.position as Point)
            out.center = centre
            if (host) {
                const projected = projectToLine(centre, host.start, host.end)
                out.wall_id = wallId
                out.position = round(Math.max(0, Math.min(1, projected.t)))
                out.direction_deg = round(toDeg(Math.atan2(host.end.y - host.start.y, host.end.x - host.start.x)))
                out.wall_thickness = host.thickness
            } else {
                out.direction_deg = 0
                if (wallId) warnings.push({ code: 'opening_wall_unresolved', params: { wall: wallId } })
            }
        } else if (host && num(o.position) !== undefined) {
            out.wall_id = wallId
            out.position = round(Math.max(0, Math.min(1, num(o.position) as number)))
            out.wall_thickness = host.thickness
        } else {
            warnings.push({ code: 'opening_dropped', params: { index: i + 1 } })
            continue
        }
        if (typeof o.operation === 'string' && OPERATIONS.has(o.operation)) out.operation = o.operation as never
        if (typeof o.swing === 'string' && SWINGS.has(o.swing)) out.swing = o.swing as never
        if (o.leaves === 2) out.leaves = 2
        // The exchange format allows any angle; only the half-turn that flips
        // the fixture onto the other wall face means anything here.
        const rotation = num(o.rotation)
        if (rotation !== undefined && Math.abs(((rotation % 360) + 360) % 360 - 180) < 1) out.rotation = 180
        if (o.properties) out.properties = o.properties
        openings.push(out)
    }

    const equipments: NonNullable<VectorValue['equipments']> = []
    for (const [i, q] of asArray<RawEquipment>(doc.equipments).entries()) {
        if (!isPoint(q.position)) {
            warnings.push({ code: 'equipment_dropped', params: { index: i + 1 } })
            continue
        }
        const kind = typeof q.kind === 'string' ? q.kind : typeof q.type === 'string' ? q.type : undefined
        const width = num(q.dimensions?.width)
        const depth = num(q.dimensions?.depth)
        equipments.push({
            id: idFor(q.id, 'equipment', i),
            ...(kind ? { kind } : {}),
            position: scalePoint(q.position as Point),
            rotation_deg: round(num(q.rotation) ?? num(q.rotation_deg) ?? 0),
            ...(width !== undefined && depth !== undefined
                ? { dimensions: { width: round(width * k), depth: round(depth * k) } }
                : {}),
        })
    }

    if (!walls.length && !spaces.length && !openings.length && !equipments.length) {
        return { ok: false, errors: [{ code: 'nothing_to_import' }] }
    }

    const title = typeof doc.metadata?.name === 'string' && doc.metadata.name.trim()
        ? doc.metadata.name.trim()
        : 'floorplan'

    const value: VectorValue = { version: 'floorplan-v1', units: 'mm', title }
    if (walls.length) value.walls = walls
    if (spaces.length) value.spaces = spaces
    if (openings.length) value.openingEquipments = openings
    if (equipments.length) value.equipments = equipments

    return {
        ok: true,
        result: {
            value,
            title,
            counts: { walls: walls.length, spaces: spaces.length, openings: openings.length, equipments: equipments.length },
            warnings,
        },
    }
}

/** Every point of an imported value, for the auto-fit transform (§7). */
export function importedPoints(value: VectorValue): Point[] {
    const pts: Point[] = []
    for (const w of value.walls ?? []) pts.push(w.start, w.end)
    for (const s of value.spaces ?? []) pts.push(...(s.boundary ?? []))
    for (const o of value.openingEquipments ?? []) if (o.center) pts.push(o.center)
    for (const q of value.equipments ?? []) pts.push(q.position)
    return pts
}

function asArray<T>(v: unknown): T[] {
    return Array.isArray(v) ? (v as T[]) : []
}

const round = (v: number): number => Math.round(v * 1000) / 1000
